import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@material-ui/core';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { produce } from 'immer';
import Content from '../../components/Layout/Content';
import RowsIterator from './components/RowsIterator';
import ShipmentDialog from '../../components/Shipment/ShipmentDialog';
import OpenWithExternalApp from '../../components/OpenWithExternalApp';
import { REQUEST, REQUEST_STATUS } from '../../constants';
import { isLabPartner, isRetainerPatient } from '../../helpers';
import { LAYOUT_BATCH_MODE, LAYOUT_BUNDLING, LAYOUT_DEFAULT, LAYOUT_RETAINERS } from './layout';
import BaseDataTable from '../../components/DataTable';
import styles from './styles';
import mustNotTriggerCollapseToggling from './helpers/mustNotTriggerCollapseToggling';
import withIdleDetector from '../../hooks/withIdleDetector';
import Filters from './components/Filters';
import getActions from './helpers/getActions';
import getColumns from './helpers/getColumns';
import {
  FETCH_PATIENTS_PREFIX,
  initialState,
  setPatientsPageLayout
} from '../../reducers/patients';
import { SORT_VALUE as BUNDLE_SHIPMENT_SORT_VALUE } from './components/ShipmentBundling/constants';
import createLoadingSelector from '../../selectors/loading';
import { isShipmentBundlingEnabledSelector } from '../../selectors/patients';
import { isRetainerRequestFilter } from '../../components/PatientsFilter/helpers';

const useStyles = makeStyles(styles);

const REFRESH_INTERVAL = 10; // seconds

const DataTable = ({
  globalParams, // react state
  additionalParams, // local state
  setAdditionalParams,
  handleUpdateData,
  idle,
  layout,
  setLayout
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  const refreshTimes = useRef(0);

  const [batchedCases, setBatchedCases] = useState([]);
  const [collapsed, setCollapsed] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [collapsedAll, setCollapsedAll] = useState(false);

  const rows = useSelector(state => state.patients.items);
  const total = useSelector(state => state.patients.total);
  const isLoading = useSelector(state => createLoadingSelector([FETCH_PATIENTS_PREFIX])(state));
  const isShipmentBundlingEnabled = useSelector(state => isShipmentBundlingEnabledSelector(state));
  const is3PP = useSelector(state => isLabPartner(state.auth.user));

  const handleReset = (params = {}) => {
    setBatchedCases([]);
    const layoutDefaults =
      layout === LAYOUT_BUNDLING
        ? {
            shipping: true,
            shipped: null,
            orderBy: BUNDLE_SHIPMENT_SORT_VALUE,
            order: 'asc'
          }
        : {};
    setAdditionalParams({
      params: {
        ...initialState.globalParams,
        ...initialState.customGlobalParams,
        ...layoutDefaults,
        ...params
      },
      resetPage: true
    });
  };

  const handleSort = useCallback(
    ({ property, direction }) => {
      setAdditionalParams(
        produce(additionalParams, draft => {
          if (!direction) {
            draft.params.order = initialState.globalParams.order;
            draft.params.orderBy = initialState.globalParams.orderBy;
          } else {
            draft.params.order = direction;
            draft.params.orderBy = property;
          }
          draft.resetPage = true;
        })
      );
    },
    [additionalParams, setAdditionalParams]
  );

  const handleFilterChange = (newValues, resetPage = false) => {
    setCollapsed([]);
    setAdditionalParams(
      produce(additionalParams, draft => {
        draft.params = {
          ...additionalParams.params,
          ...newValues
        };
        draft.resetPage = resetPage;
      })
    );
  };

  // eslint-disable-next-line no-shadow
  const refresh = useCallback(() => {
    refreshTimes.current += 1;
    setAdditionalParams(
      produce(additionalParams, draft => {
        draft.params = { ...additionalParams.params };
      })
    );
    setBatchedCases([]);
  }, [setAdditionalParams, additionalParams, setBatchedCases]);

  const isCollapsed = patientId => {
    return collapsed.find(item => patientId === item) !== undefined;
  };

  const toggleCollapse = patientId => {
    if (isCollapsed(patientId)) {
      setCollapsed(collapsed.filter(item => item !== patientId));
    } else {
      setCollapsed([...collapsed, patientId]);
    }
  };

  const rowsPropsProvider = row => {
    const props = {};
    if (isCollapsed(row.id)) {
      props.className = classes.collapsedRow;
    }
    props.onMouseUp = e => {
      if (!mustNotTriggerCollapseToggling(e.target)) {
        toggleCollapse(row.id);
      }
    };
    return props;
  };

  const handleTrackingNumberSearch = trackingNumber => {
    if (is3PP) {
      handleReset({
        trackingNumber,
        activeRequestOnly: false,
        requestTypeStatus: [`${REQUEST.PRINT}.${REQUEST_STATUS.COMPLETED}`]
      });
      return;
    }
    const shippedParams = {
      trackingNumber,
      shipping: null,
      shipped: true,
      orderBy: BUNDLE_SHIPMENT_SORT_VALUE,
      order: 'asc'
    };
    if (layout === LAYOUT_BUNDLING) {
      handleFilterChange(shippedParams, true);
      return;
    }
    setLayout(LAYOUT_BUNDLING, { ...shippedParams, page: 1 });
  };

  const externalAppOpener = useMemo(
    () => ({
      condition: row => !isRetainerPatient(row),
      CustomComponent: ({ row }) => {
        return (
          <OpenWithExternalApp
            patient={row}
            fileId={row.ios_file_id}
            buttonProps={{
              name: 'open',
              title: 'Open DIBS',
              className: classes.compactButton,
              disabled: !row.has_ios_file && !row.has_stl_file,
              size: 'small',
              variant: 'contained'
            }}
          />
        );
      },
      // eslint-disable-next-line react/prop-types
      Wrapper: ({ row, children }) => {
        let badgeLabel = null;
        const classNames = [classes.dibsButtonBadge];
        if (row.has_ios_file) {
          badgeLabel = 'IOS';
          classNames.push(classes.dibsButtonBadgeIos);
        } else if (row.has_stl_file) {
          badgeLabel = 'STL';
          classNames.push(classes.dibsButtonBadgeStl);
        }
        return (
          <Badge className={classNames.join(' ')} badgeContent={badgeLabel}>
            {children}
          </Badge>
        );
      }
    }),
    [classes]
  );

  useEffect(() => {
    if (!idle || !autoRefresh) {
      refreshTimes.current = 0;
      return () => {};
    }
    if (refreshTimes.current === 0) {
      refresh();
      return () => {};
    }

    const timeout = setTimeout(refresh, REFRESH_INTERVAL * 1000);

    return () => clearTimeout(timeout);
  }, [idle, refresh, autoRefresh]);

  useEffect(() => {
    if (
      globalParams.orderBy === BUNDLE_SHIPMENT_SORT_VALUE &&
      globalParams.orderBy !== additionalParams.params.orderBy
    ) {
      handleSort({
        property: BUNDLE_SHIPMENT_SORT_VALUE,
        direction: 'asc'
      });
    }
  }, [globalParams.orderBy, additionalParams.params.orderBy, handleSort]);

  useEffect(() => {
    if (collapsedAll) {
      setCollapsed([...rows.map(r => r.id)]);
    } else {
      setCollapsed([]);
    }
  }, [rows, collapsedAll, setCollapsed]);

  // eslint-disable-next-line react/prop-types
  const { requestTypeStatus } = additionalParams.params;
  const isRetainerFilter = isRetainerRequestFilter({ requestTypeStatus });

  // Selecting "Retainers" swaps in a layout that omits the incompatible controls;
  // leaving it (another type, clear, or reset) restores the default layout.
  useEffect(() => {
    if (isRetainerFilter && layout === LAYOUT_DEFAULT) {
      setLayout(LAYOUT_RETAINERS, { requestTypeStatus });
    } else if (!isRetainerFilter && layout === LAYOUT_RETAINERS) {
      setLayout(LAYOUT_DEFAULT, { requestTypeStatus });
    }
  }, [isRetainerFilter, layout, requestTypeStatus, setLayout]);

  const actions =
    layout === LAYOUT_BATCH_MODE
      ? []
      : getActions({
          externalAppOpener,
          history,
          dispatch,
          classes,
          collapsed,
          setCollapsed,
          handleTrackingNumberSearch,
          isCollapsed,
          isShipmentBundlingEnabled
        });

  let selectedColumns;
  if (layout === LAYOUT_BATCH_MODE) {
    selectedColumns = ['batching', 'patient', 'print_parts', 'requests', 'shipping'];
  } else if (layout === LAYOUT_RETAINERS) {
    // retainers have no tech workflow, hence no production time
    selectedColumns = ['patient', 'date', 'requests', 'shipping'];
  } else if (layout === LAYOUT_BUNDLING) {
    selectedColumns = ['patient', 'date', 'production_time', 'requests', 'shipping'];
  } else {
    selectedColumns = ['patient', 'date', 'production_time', 'requests', 'batch', 'shipping'];
  }

  const columns = getColumns(selectedColumns, {
    isCollapsed,
    additionalParams,
    handleTrackingNumberSearch,
    batchedCases,
    setBatchedCases,
    isShipmentBundlingEnabled
  });

  const renderFilters = () => {
    return (
      <>
        <Filters
          values={additionalParams.params}
          onChange={handleFilterChange}
          onReset={handleReset}
          onSort={handleSort}
          layout={layout}
          setLayout={setLayout}
          refresh={refresh}
          autoRefresh={autoRefresh}
          setAutoRefresh={setAutoRefresh}
          collapsedAll={collapsedAll}
          setCollapsedAll={setCollapsedAll}
          batchedCases={batchedCases}
          setBatchedCases={setBatchedCases}
        />
      </>
    );
  };

  return (
    <Content filters={renderFilters()} className={classes.compact}>
      <BaseDataTable
        isLoading={isLoading}
        columns={columns}
        rows={rows}
        total={total}
        updateData={handleUpdateData}
        defaultOrderBy={additionalParams.params.orderBy}
        additionalParams={additionalParams}
        showVerticalLines
        rowsPerPageOptions={[10, 25, 50, 100]}
        defaultRowsPerPage={50}
        customActions={actions}
        globalParams={globalParams}
        onSortChange={handleSort}
        rowPropsProvider={rowsPropsProvider}
        RowsIterator={RowsIterator}
        actionCellProps={{
          style: { minWidth: 210 }
        }}
      />
      <ShipmentDialog />
    </Content>
  );
};

DataTable.propTypes = {};

export default connect(
  (state, props) => {
    return {
      layout: state.patients.layout
    };
  },
  dispatch => {
    return {
      setLayout: (layout, params) => dispatch(setPatientsPageLayout(layout, params))
    };
  }
)(withIdleDetector(DataTable, REFRESH_INTERVAL));
